import { FC, useMemo } from 'react';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftDeliverySummary } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import {
  getCouponStats,
  getDeliveryStats,
  getIssueStats,
  getOrderType,
  paymentStatusLabel,
} from '../utils/giftOrder.constants';
import { computeCrossCouponHashOverlap } from './couponDetail/kakaoShareOverlap';
import { DeliveryModeBadge, OrderTypeBadge } from './OrderBadges';

export const CouponStatsCell: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (coupons: GiftCouponSummary[]) => void;
}> = ({ order, onCouponClick }) => {
  const couponStats = getCouponStats(order);

  const kakaoRisk = useMemo(() => {
    const coupons = order.coupons ?? [];
    if (coupons.length === 0) {
      return {
        multiRoomCount: 0,
        crossCouponCouponCount: 0,
        crossCouponGroupCount: 0,
        multiRoomCoupons: [] as GiftCouponSummary[],
      };
    }
    const multiRoomCoupons = coupons.filter((c) => (c.shareKakaoCount ?? 0) >= 2);
    const overlap = computeCrossCouponHashOverlap(coupons);
    return {
      multiRoomCount: multiRoomCoupons.length,
      crossCouponCouponCount: overlap.overlapCouponIds.size,
      crossCouponGroupCount: overlap.groups.length,
      multiRoomCoupons,
    };
  }, [order.coupons]);

  if (!couponStats || !order.coupons || order.coupons.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const hasKakaoRisk =
    kakaoRisk.multiRoomCount > 0 || kakaoRisk.crossCouponGroupCount > 0;

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        onClick={() => onCouponClick(order.coupons ?? [])}
        className="cursor-pointer transition-opacity hover:opacity-80"
      >
        <span className="inline-flex items-center gap-0.5 text-xs">
          <span className="font-semibold text-gray-700">
            {couponStats.activeCount}
            {couponStats.reissuedCount > 0 && (
              <span className="text-[10px] font-normal text-teal-600">
                (재{couponStats.reissuedCount})
              </span>
            )}
            {couponStats.additionalCount > 0 && (
              <span className="text-[10px] font-normal text-amber-600">
                (+{couponStats.additionalCount})
              </span>
            )}
            {couponStats.disposedCount > 0 && (
              <span className="text-[10px] font-normal text-slate-400">
                폐{couponStats.disposedCount}
              </span>
            )}
            {couponStats.canceledCount > 0 && (
              <span className="text-[10px] font-normal text-red-400">
                취{couponStats.canceledCount}
              </span>
            )}
          </span>
          <span className="text-gray-300">/</span>
          <span
            className={`font-semibold ${
              couponStats.notSharedCount > 0 ? 'text-orange-600' : 'text-gray-400'
            }`}
          >
            {couponStats.notSharedCount}
          </span>
          <span className="text-gray-300">/</span>
          <span
            className={`font-semibold ${
              couponStats.unusedCount > 0 ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            {couponStats.unusedCount}
          </span>
          <span className="text-gray-300">/</span>
          <span
            className={`font-semibold ${
              couponStats.deliveredCount > 0 ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            {couponStats.deliveredCount}
          </span>
          {couponStats.pendingLaterCount > 0 && (
            <span
              className="ml-1 inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700"
              title={`나중에 받기 — 교환신청은 안 했지만 수신번호 인증까지 마친 쿠폰 ${couponStats.pendingLaterCount}건`}
            >
              나중 {couponStats.pendingLaterCount}
            </span>
          )}
        </span>
      </button>
      {hasKakaoRisk && (
        <div className="flex flex-wrap items-center gap-1">
          {kakaoRisk.multiRoomCount > 0 && (
            <button
              type="button"
              onClick={() => onCouponClick(order.coupons ?? [])}
              className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-200"
              title={(() => {
                const detail = kakaoRisk.multiRoomCoupons
                  .map((c) => `#${c.seq} ${c.code} (${c.shareKakaoCount ?? 0}개 카톡방)`)
                  .join(', ');
                return `중복노출 — 이 주문에서 ${
                  kakaoRisk.multiRoomCount
                }개 쿠폰이 2개 이상 서로 다른 카톡방으로 전달됨${
                  detail ? `: ${detail}` : ''
                }`;
              })()}
            >
              <span>⚠</span>
              <span>중복노출 {kakaoRisk.multiRoomCount}</span>
            </button>
          )}
          {kakaoRisk.crossCouponGroupCount > 0 && (
            <button
              type="button"
              onClick={() => onCouponClick(order.coupons ?? [])}
              className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-200"
              title={`다건수신 (주문내) — 이 주문의 ${kakaoRisk.crossCouponCouponCount}개 쿠폰이 동일한 카톡방으로 함께 수신됨 (${kakaoRisk.crossCouponGroupCount}개 채팅방)`}
            >
              <span>⚠</span>
              <span>다건수신(주문내) {kakaoRisk.crossCouponCouponCount}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const DeliveryStatsCell: FC<{
  order: GiftOrderForAdmin;
  onDeliveryClick: (deliveries: GiftDeliverySummary[]) => void;
}> = ({ order, onDeliveryClick }) => {
  const deliveryStats = getDeliveryStats(order);
  if (!deliveryStats) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <button
      onClick={() => onDeliveryClick((order.deliveries ?? []) as GiftDeliverySummary[])}
      className="cursor-pointer transition-opacity hover:opacity-80"
    >
      <span className="inline-flex items-center gap-0.5 text-xs">
        <span className="font-semibold text-gray-700">
          {deliveryStats.canceledCount > 0 ? (
            <>
              {deliveryStats.activeCount}
              <span className="text-[10px] font-normal text-red-400">
                ({deliveryStats.totalCount}-{deliveryStats.canceledCount})
              </span>
            </>
          ) : (
            deliveryStats.totalCount
          )}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.waitingAddressCount > 0 ? 'text-orange-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.waitingAddressCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.preparingCount > 0 ? 'text-yellow-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.preparingCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.shippingCount > 0 ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.shippingCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.deliveredCount > 0 ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.deliveredCount}
        </span>
      </span>
    </button>
  );
};

export const OrderRow: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (coupons: GiftCouponSummary[]) => void;
  onDeliveryClick: (deliveries: GiftDeliverySummary[]) => void;
  onStatusClick?: (order: GiftOrderForAdmin) => void;
}> = ({ order, onCouponClick, onDeliveryClick, onStatusClick }) => {
  const memberName = order.bugo?.member?.fullName || '-';
  const memberPhone = order.bugo?.member?.phoneNumber || '-';
  const deceasedName = order.bugo?.fevent?.deceasedInfo?.name || '-';
  const bugoBrandName =
    typeof order.bugoBrand === 'object' ? order.bugoBrand.bugoAgency?.corpName : '-';
  const totalItems = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;

  const orderPrice = order.orderDetails[0]?.giftItemEmbed.priceRetail;
  const orderType = getOrderType(order);
  const issueStats = getIssueStats(order);

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {(() => {
          const d = new Date(order.paymentDetail.purchasedAt || order.createdAt);
          const yy = String(d.getFullYear()).slice(2);
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const hh = String(d.getHours()).padStart(2, '0');
          const mi = String(d.getMinutes()).padStart(2, '0');
          return (
            <div>
              <div>{`${yy}/${mm}/${dd}`}</div>
              <div>{`${hh}:${mi}`}</div>
            </div>
          );
        })()}
      </td>
      <td className="px-3 py-4 text-sm">
        <p className="font-medium text-indigo-600 hover:text-indigo-900">
          {orderName} {orderPrice?.toLocaleString()}원
        </p>
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-700">수량: {totalItems}개</p>
          {order.paymentDetail.receiptUrl && (
            <button
              type="button"
              className="ml-1 rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  order.paymentDetail.receiptUrl,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            >
              영수증
            </button>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <div className="flex items-center gap-1">
          <OrderTypeBadge orderType={orderType} />
          {(orderType === 'delivery' || orderType === 'mixed') && (
            <DeliveryModeBadge mode={order.deliveryMode} />
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <p className="text-gray-900">
          {formatPrice(order.paymentDetail.paymentByCash)}원
        </p>
        {(order.paymentDetail.cancelledTotalPrice ?? 0) > 0 && (
          <p className="text-xs text-red-500">
            -{formatPrice(order.paymentDetail.cancelledTotalPrice ?? 0)}원 취소
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{memberName}</p>
        <p className="text-xs">{phoneNumberStringFormator(memberPhone)}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {deceasedName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {bugoBrandName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <CouponStatsCell order={order} onCouponClick={onCouponClick} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <DeliveryStatsCell order={order} onDeliveryClick={onDeliveryClick} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <button
          type="button"
          onClick={() => onStatusClick?.(order)}
          className={`inline-flex cursor-pointer rounded-full px-2 text-xs font-semibold leading-5 transition-opacity hover:opacity-70 ${
            order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
            order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed
              ? 'bg-green-100 text-green-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested
              ? 'bg-orange-100 text-orange-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.Canceled
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {paymentStatusLabel[order.paymentDetail.status] || order.paymentDetail.status}
        </button>
        {order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested &&
          order.paymentDetail.cancelReason && (
            <p
              className="mt-0.5 max-w-[120px] truncate text-[10px] text-orange-600"
              title={order.paymentDetail.cancelReason}
            >
              {order.paymentDetail.cancelReason}
            </p>
          )}
        {order.paymentDetail.status === GiftOrderPaymentStatus.Canceled &&
          order.paymentDetail.cancelReason && (
            <p
              className="mt-0.5 max-w-[120px] truncate text-[10px] text-red-500"
              title={order.paymentDetail.cancelReason}
            >
              {order.paymentDetail.cancelReason}
            </p>
          )}
        {issueStats && (
          <div className="mt-1 flex flex-wrap gap-0.5">
            {issueStats.issuedCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                이슈생성 {issueStats.issuedCount}
              </span>
            )}
            {issueStats.requestedCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                이슈신청 {issueStats.requestedCount}
              </span>
            )}
            {issueStats.completedCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                이슈완료 {issueStats.completedCount}
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
